import { useMemo } from 'react';
import { useFormik, FormikConfig, FormikHelpers, FormikValues } from 'formik';
import { useAsyncValidation } from '@/contexts/AsyncValidationContext';

/**
 * Enhanced version of useFormik that automatically handles async validation state.
 * This hook:
 * - Automatically merges async validation errors with sync errors
 * - Prevents form submission when async validations are pending (as a safety net)
 * - Provides a submitDisabled property that MUST be used to disable submit buttons
 * - Maintains all standard Formik functionality
 *
 * Important: Always use formik.submitDisabled for your submit button's disabled prop
 * to prevent users from submitting while validations are pending.
 */
export function useFormikWithAsyncValidation<
  TValues extends FormikValues = FormikValues,
>(config: FormikConfig<TValues>) {
  const { errors: asyncErrors, hasAnyPendingValidations } =
    useAsyncValidation();

  const configWithAsyncValidate: FormikConfig<TValues> = useMemo(
    () => ({
      ...config,
      validate: async (values) => {
        const syncErrors = config.validate ? await config.validate(values) : {};
        return { ...syncErrors, ...asyncErrors };
      },
      onSubmit: async (
        values: TValues,
        formikHelpers: FormikHelpers<TValues>,
      ) => {
        if (hasAnyPendingValidations) {
          formikHelpers.setSubmitting(false);
          return;
        }

        return config.onSubmit(values, formikHelpers);
      },
    }),
    [config, asyncErrors, hasAnyPendingValidations],
  );

  const formik = useFormik<TValues>(configWithAsyncValidate);

  return {
    ...formik,
    isValidating: hasAnyPendingValidations,
    submitDisabled: formik.isSubmitting || hasAnyPendingValidations,
  };
}
